// widgets/location-popover/lib/getLocationPopoverState.ts

import type { IPoint } from '@/_store/common/types';
import type { IOrderType, ISavedBranch } from '@/_store/types';

export type GeoStatus =
  | 'accepted'
  | 'pending'
  | 'rejected'
  | 'unknown'
  | string;

export type LocationPopoverMode = 'points' | 'saved' | 'ask';

export type LocationPopoverState = {
  mode: LocationPopoverMode;
  triggerLabelKey:
    | 'address'
    | 'branch'
    | 'pending'
    | 'enter_address'
    | 'select_branch';
  triggerLabelValue?: string;
};

type Input = {
  orderType: IOrderType | null;
  geolocation: GeoStatus;
  activePoint: IPoint | null;
  branch: ISavedBranch | null;
};

export const getLocationPopoverState = ({
  orderType,
  geolocation,
  activePoint,
  branch,
}: Input): LocationPopoverState => {
  // Null order type is treated as delivery (default).
  const isPickup = orderType?.type === 'self-pickup';
  const isDelivery = !isPickup;

  const addressLabel = activePoint?.name || activePoint?.address || '';
  const branchLabel = branch?.name || '';

  const hasAddress = Boolean(addressLabel);
  const hasBranch = Boolean(branchLabel);

  // ✅ If geolocation accepted => show Points selector
  if (geolocation === 'accepted') {
    return {
      mode: 'points',
      triggerLabelKey: isPickup ? 'branch' : 'address',
      triggerLabelValue: isPickup ? branchLabel : addressLabel,
    };
  }

  // ✅ Pending => show pending label, still popover may show ask/saved
  if (geolocation === 'pending') {
    return {
      mode: hasAddress || hasBranch ? 'saved' : 'ask',
      triggerLabelKey: 'pending',
    };
  }

  // ✅ Not accepted: show saved if we already have something
  if (isDelivery && hasAddress) {
    return {
      mode: 'saved',
      triggerLabelKey: 'address',
      triggerLabelValue: addressLabel,
    };
  }

  if (isPickup && hasBranch) {
    return {
      mode: 'saved',
      triggerLabelKey: 'branch',
      triggerLabelValue: branchLabel,
    };
  }

  // ✅ Empty fallback state
  return {
    mode: 'ask',
    triggerLabelKey: isPickup ? 'select_branch' : 'enter_address',
  };
};
